import React, { useState, useRef } from 'react';
import ActionBar from './ActionBar';
import { StyledResizable, StyledTable } from './styles';
import { Button, Input, Space } from 'antd';
import Highlighter from 'react-highlight-words';
import { SearchOutlined } from '@ant-design/icons';
import { alphabetSort } from 'src/utils/helpers';
import useWindowSize from 'src/utils/hooks/useWindowSize';
import { LongTextContainer } from './styles';

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <StyledResizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{
        enableUserSelectHack: false,
      }}
    >
      <th {...restProps} />
    </StyledResizable>
  );
};

const CustomTable = ({ columns: defaultColumns, loading, data, onRefresh, onAdd, onDelete }) => {
  const [columns, setColumns] = useState(defaultColumns);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { windowHeight } = useWindowSize();

  // handle searching for columns
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const searchInput = useRef(null);
  const [currentDataSource, setCurrentDataSource] = useState([{}]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex, render) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          onFocus={() => setIsTyping(true)}
          onBlur={() => setIsTyping(false)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
            type="link"
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          {/* <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button> */}
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString()?.toLowerCase()?.includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text, record) => {
      return (
        render
          ? render(text, record)
          : <LongTextContainer>
            {searchedColumn === dataIndex ? (
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                style={{ margin: 'auto' }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
              />
            ) : <div style={{ margin: 'auto' }}>{text}</div>}
          </LongTextContainer>
      )
    }
  });

  // handle clearing search, filters and sorters
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const hasFilters = (() => {
    const isEmptyFilter = Object.values(filteredInfo).every(filter => filter == null);
    const isEmptySorter = sortedInfo?.order == null;
    return !isEmptyFilter || !isEmptySorter;
  })();

  const handleChange = (pagination, filters, sorter, extra) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    setCurrentDataSource(extra.currentDataSource);
  };

  const clearAllFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setSearchText('');
    setCurrentDataSource(Object.values(data));
  };

  // handle resizing columns
  const handleResize = (index) => (_, { size }) => {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], width: size.width };
    setColumns(newColumns);
  };

  const mergeColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
    ...((col.searchable) && getColumnSearchProps(col.dataIndex, col.render)),
    sorter: col.key === 'actions' ? null : (isTyping ? 0 : (a, b) => alphabetSort(a, b, col.dataIndex)),  // doesn't render sorter for actions column and disable sort function from being triggered as user press enter when searching
    sortOrder: sortedInfo.field === col.dataIndex ? sortedInfo.order : null,
    filteredValue: filteredInfo[col.dataIndex] || null,
    ellipsis: col.ellipsis ? (searchedColumn === col.dataIndex && searchText?.length > 0 ? false : true) : false
  }));

  return (
    <div>
      <ActionBar
        data={Object.values(data)}
        currentDataSource={currentDataSource}
        selectedRowKeys={selectedRowKeys}
        onRefresh={onRefresh}
        hasFilters={hasFilters}
        clearAllFilters={clearAllFilters}
        onAdd={onAdd}
        onDelete={(selectedIds) => {
          onDelete(selectedIds);
          setSelectedRowKeys([]);
        }}
      />
      <StyledTable
        bordered
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        columns={mergeColumns}
        loading={loading}
        dataSource={Object.values(data)}
        rowKey={record => record.id}
        rowSelection={{
          fixed: true,
          selectedRowKeys,
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
          },
        }}
        style={{ width: '100%', height: '100%' }}
        scroll={{
          x: 0,
          y: windowHeight - 222,
        }}
        pagination={{
          position: ['topRight'],
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleChange}
      />
    </div>
  )
}

export default CustomTable;